import { BasePayloadMapper, orEmpty, spreadIf } from '@ps/utils';
import {
  mapSurveyLocalization,
  Quota,
  QuotaCriteria,
  SurveyMetadataForm,
} from '@ps/survey-utils';

import {
  UnicornCriteriaDto,
  UnicornQuotaDto,
  UnicornRequestDto,
} from '../dtos/unicorn.dto';

export interface UnicornPayloadInput {
  readonly metadata: SurveyMetadataForm;
  readonly quotas: readonly Quota[];
}

/** The service matches on codes and ranges only — names carry no meaning to it. */
function toCriterion(criterion: QuotaCriteria): UnicornCriteriaDto {
  const rangeSets = criterion.rangeSets ?? [];

  return {
    qualification_code: criterion.qualificationCode,
    ...spreadIf(!!criterion.conditionCodes?.length, {
      condition_codes: [...criterion.conditionCodes],
    }),
    ...spreadIf(!!rangeSets.length, {
      range_sets: rangeSets.map(({ from, to, units }) => ({
        from,
        to,
        units,
      })),
    }),
  };
}

function toQuota(quota: Quota): UnicornQuotaDto {
  return {
    buyer_quota_id: quota.buyerQuotaId,
    criteria: quota.criteria.map(toCriterion),
  };
}

export class UnicornPayloadMapper extends BasePayloadMapper<
  UnicornPayloadInput,
  UnicornRequestDto
> {
  public toPayload({
    metadata,
    quotas,
  }: UnicornPayloadInput): UnicornRequestDto {
    return {
      survey_localization: this._localization(metadata),
      quotas: quotas.map(toQuota),
    };
  }

  private _localization(metadata: SurveyMetadataForm): string {
    return mapSurveyLocalization(
      orEmpty(metadata.selectedCountries[0]),
      orEmpty(metadata.selectedLanguages[0]),
    );
  }
}
